import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight, X, Filter, Grid, List, ChevronDown, Star, Eye } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Separate input state for search
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(12); // Show 12 products initially
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [selectedProductName, setSelectedProductName] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('name'); // 'name', 'price', 'category'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const [showScrollTop, setShowScrollTop] = useState(false);

  const categories = ['All', 'Air Filters', 'Fuel Filters', 'Oil Filters', 'Hydraulic Return Filters', 'Coolant Filters', 'Cabin Filters'];

  // Hard-disable horizontal scrolling on this page (mobile safety)
  useEffect(() => {
    const previousBodyOverflowX = document.body.style.overflowX;
    const previousHtmlOverflowX = document.documentElement.style.overflowX;
    document.body.style.overflowX = 'hidden';
    document.documentElement.style.overflowX = 'hidden';
    return () => {
      document.body.style.overflowX = previousBodyOverflowX;
      document.documentElement.style.overflowX = previousHtmlOverflowX;
    };
  }, []);

  // Fix double /media/ URLs
  const fixImageUrl = (url) => {
    if (!url) return url;
    // Handle PHP backend image paths
    if (url.startsWith('uploads/')) {
      return `/backend-php/${url}`;
    }
    // Handle Django double media issue
    if (url.includes('/media/media/')) {
      return url.replace('/media/media/', '/media/');
    }
    // Handle absolute URLs
    if (url.startsWith('http')) {
      return url;
    }
    // Default fallback
    return url;
  };

  // Image modal functions
  const handleImageClick = (imageUrl, productName) => {
    setSelectedImage(imageUrl);
    setSelectedProductName(productName);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage('');
    setSelectedProductName('');
  };


  // Load products from PHP backend
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        
        // Load from PHP API (absolute URL in production to avoid rewrite/path issues)
        const apiUrl =
          typeof window !== 'undefined' && window.location && window.location.hostname.includes('amazonfiltration.co.ke')
            ? 'https://amazonfiltration.co.ke/backend-php/api/products.php?t=' + Date.now()
            : '/backend-php/api/products.php?t=' + Date.now();

        const response = await fetch(apiUrl, {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        if (response.ok) {
          let data;
          try {
            data = await response.json();
          } catch (parseErr) {
            // Not JSON (likely HTML/404). Log raw text to help diagnose
            const txt = await response.text();
            console.log('Products API returned non-JSON:', txt?.slice(0, 500));
            throw parseErr;
          }
          console.log('Products loaded from PHP API:', data);
          console.log('Response status:', response.status);
          console.log('Response headers:', response.headers);
          
          // Accept both shapes: { success, products } and { results, count }
          if (data && data.success && Array.isArray(data.products)) {
            setProducts(data.products);
            console.log(`Loaded ${data.products.length} products from PHP API`);
            console.log('Products loaded:', data.products.map(p => ({ id: p.id, name: p.name })));
          } else if (data && Array.isArray(data.results)) {
            setProducts(data.results);
            console.log(`Loaded ${data.products.length} products from PHP API`);
          } else if (Array.isArray(data)) {
            // In case API returns raw list
            setProducts(data);
          } else {
            // No products found - show empty state
            console.log('No products found in PHP API');
            console.log('Data structure:', data);
            setProducts([]);
          }
        } else {
          // API not responding - show empty state
          console.log('PHP API not responding');
          console.log('Response status:', response.status);
          try {
            const txt = await response.text();
            console.log('Response text:', txt?.slice(0, 500));
          } catch {}
          setProducts([]);
        }
        
      } catch (error) {
        console.error('Error loading products:', error);
        setError('Error loading products. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [currentPage, productsPerPage, selectedCategory]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('Auto-refreshing products...');
      // Trigger a re-fetch by updating a dependency
      setCurrentPage(prev => prev);
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Show scroll-to-top FAB
  useEffect(() => {
    const onScroll = () => {
      setShowScrollTop(window.scrollY > 240);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Refresh products when page becomes visible (user switches back to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('Page became visible, refreshing products...');
        setCurrentPage(prev => prev);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleSearchSubmit = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  // Filter and sort products
  const filteredProducts = products.filter(product => {
    const name = (product.name || '').toString();
    const description = (product.description || '').toString();
    const code = (product.code || '').toString();
    const term = (searchTerm || '').toString().toLowerCase();

    // Do not filter until user explicitly searched (term confirmed via button/enter)
    const matchesSearch = term
      ? name.toLowerCase().includes(term) ||
        description.toLowerCase().includes(term) ||
        code.toLowerCase().includes(term)
      : true;
    const matchesCategory = selectedCategory === 'All' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  }).sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'price':
        aValue = parseFloat(a.price);
        bValue = parseFloat(b.price);
        break;
      case 'category':
        aValue = a.category;
        bValue = b.category;
        break;
      default:
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Calculate pagination
  const totalFilteredProducts = filteredProducts.length;
  const totalPagesFiltered = Math.ceil(totalFilteredProducts / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchTerm]);

  // Scroll to top when page changes
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Products</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 overflow-x-hidden">
      {/* Modern Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Products</h1>
            <p className="text-xl md:text-2xl text-primary-100 max-w-4xl mx-auto leading-relaxed">
              Premium filtration solutions for automotive, industrial, and marine applications
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm">
              <div className="flex items-center bg-primary-500 bg-opacity-30 px-4 py-2 rounded-full">
                <Star className="w-4 h-4 mr-2" />
                High Quality
              </div>
              <div className="flex items-center bg-primary-500 bg-opacity-30 px-4 py-2 rounded-full">
                <Star className="w-4 h-4 mr-2" />
                Professional Grade
              </div>
              <div className="flex items-center bg-primary-500 bg-opacity-30 px-4 py-2 rounded-full">
                <Star className="w-4 h-4 mr-2" />
                Trusted Brand
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Filters & Search */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-xl p-6 mb-8 border border-gray-100 sticky top-0 z-30">
          {/* Search Bar */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 sm:w-6 sm:h-6" />
                <input
                  type="text"
                  placeholder="Search by name, code, or description..."
                  value={searchInput}
                  onChange={handleSearchInputChange}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
                  className="w-full pl-12 pr-4 py-3 sm:py-4 text-base sm:text-lg border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                />
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={handleSearchSubmit}
                  className="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 sm:px-6 py-4 rounded-xl font-semibold hover:from-primary-700 hover:to-primary-800 transition-all duration-200 flex items-center justify-center whitespace-nowrap text-sm sm:text-base"
                >
                  <Search className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Search</span>
                  <span className="sm:hidden">Go</span>
                </button>
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="bg-gray-500 text-white px-4 sm:px-6 py-4 rounded-xl font-semibold hover:bg-gray-600 transition-all duration-200 flex items-center justify-center whitespace-nowrap text-sm sm:text-base"
                  >
                    <X className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Clear</span>
                    <span className="sm:hidden">×</span>
                  </button>
                )}
              </div>
            </div>
            {searchTerm && (
              <div className="mt-3 text-sm text-gray-600">
                Showing results for: <span className="font-semibold text-primary-600">"{searchTerm}"</span>
              </div>
            )}
          </div>

          {/* Filters Row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-4 flex-1 min-w-0">
              {/* Category Filter */}
              <div className="relative flex-1 min-w-0">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none bg-white text-sm sm:text-base"
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
              </div>

              {/* Sort Options */}
              <div className="flex gap-2 flex-shrink-0">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 sm:px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none bg-white text-sm sm:text-base min-w-0"
                >
                  <option value="name">Sort by Name</option>
                  <option value="price">Sort by Price</option>
                  <option value="category">Sort by Category</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-3 sm:px-4 py-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-colors flex-shrink-0"
                  title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
                {/* Items per page */}
                <select
                  value={productsPerPage}
                  onChange={(e) => setCurrentPage(1) || null || 0}
                  className="hidden"
                />
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-sm text-gray-600 hidden sm:block">View:</span>
              <div className="flex border-2 border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-3 transition-colors ${viewMode === 'grid' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  <Grid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-3 transition-colors ${viewMode === 'list' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Quick Category Pills */}
          <div className="mt-4 flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => { setSelectedCategory(cat); setCurrentPage(1); }}
                className={`px-3 py-2 rounded-full text-sm border transition-all ${selectedCategory === cat ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-200 hover:border-primary-500 hover:bg-primary-50'}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Results summary and per-page control */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div className="text-sm text-gray-600">
            Showing <span className="font-semibold text-gray-900">{Math.min(totalFilteredProducts, Math.max(1, startIndex + 1))}-{Math.min(endIndex, totalFilteredProducts)}</span> of <span className="font-semibold text-gray-900">{totalFilteredProducts}</span> products
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-600">Per page:</span>
            <select
              value={productsPerPage}
              onChange={(e) => { const v = parseInt(e.target.value, 10) || 12; setProductsPerPage(v); setCurrentPage(1); }}
              className="px-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
            >
              <option value="8">8</option>
              <option value="12">12</option>
              <option value="16">16</option>
              <option value="24">24</option>
            </select>
          </div>
        </div>

        {/* Products Display */}
        {filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <div className="bg-white rounded-2xl shadow-lg p-12 max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              {products.length === 0 ? (
                <>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Products Available</h3>
                  <p className="text-gray-600">Products will appear here once they are added.</p>
                </>
              ) : (
                <>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Products Found</h3>
                  <p className="text-gray-600 mb-4">No products match your search criteria.</p>
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSearchInput('');
                      setSelectedCategory('All');
                    }}
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Clear filters
                  </button>
                </>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Grid View */}
            {viewMode === 'grid' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
                {currentProducts.map((product) => (
                  <div key={product.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                    <div 
                      className="relative cursor-pointer group h-44 sm:h-48 overflow-hidden"
                      onClick={() => handleImageClick(fixImageUrl(product.image), product.name)}
                    >
                      <img
                        src={fixImageUrl(product.image) || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjZjhmOWZhIi8+CjxyZWN0IHg9IjE1MCIgeT0iMTAwIiB3aWR0aD0iMTAwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjNmM3NTdkIi8+Cjx0ZXh0IHg9IjIwMCIgeT0iMjIwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNmM3NTdkIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiPk5vIEltYWdlPC90ZXh0Pgo8L3N2Zz4K'}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                        onError={(e) => {
                          console.log('Image failed to load:', product.image);
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjZjhmOWZhIi8+CjxyZWN0IHg9IjE1MCIgeT0iMTAwIiB3aWR0aD0iMTAwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjNmM3NTdkIi8+Cjx0ZXh0IHg9IjIwMCIgeT0iMjIwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNmM3NTdkIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiPk5vIEltYWdlPC90ZXh0Pgo8L3N2Zz4K';
                        }}
                        decoding="async"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="bg-white bg-opacity-90 rounded-full p-3">
                            <Eye className="w-6 h-6 text-gray-800" />
                          </div>
                        </div>
                      </div>
                      <div className="absolute top-3 right-3">
                        <span className="bg-primary-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
                          {product.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-4 sm:p-5">
                      <h3 className="text-base sm:text-[17px] font-semibold text-gray-900 mb-1.5 line-clamp-2 break-words">{product.name}</h3>
                      <p className="text-xs text-gray-500 mb-2 break-all">Code: {product.code}</p>
                      <p className="text-xl sm:text-[20px] font-bold text-primary-600 mb-3">Ksh {product.price.toLocaleString()}</p>
                      <p className="text-gray-600 text-xs sm:text-sm mb-4 line-clamp-2 break-words">{product.description}</p>
                      <Link 
                        to={`/products/${product.id}`} 
                        className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-2.5 sm:py-3 px-4 rounded-lg font-semibold hover:from-primary-700 hover:to-primary-800 transition-all duration-200 flex items-center justify-center group text-sm"
                      >
                        View Details 
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* List View */}
            {viewMode === 'list' && (
              <div className="space-y-3.5">
                {currentProducts.map((product) => (
                  <div key={product.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 border border-gray-100">
                    <div className="flex flex-col md:flex-row">
                      <div 
                        className="relative cursor-pointer group md:w-72 h-40 md:h-auto overflow-hidden"
                        onClick={() => handleImageClick(fixImageUrl(product.image), product.name)}
                      >
                        <img
                          src={fixImageUrl(product.image) || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjZjhmOWZhIi8+CjxyZWN0IHg9IjE1MCIgeT0iMTAwIiB3aWR0aD0iMTAwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjNmM3NTdkIi8+Cjx0ZXh0IHg9IjIwMCIgeT0iMjIwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNmM3NTdkIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiPk5vIEltYWdlPC90ZXh0Pgo8L3N2Zz4K'}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                          onError={(e) => {
                            console.log('Image failed to load:', product.image);
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjZjhmOWZhIi8+CjxyZWN0IHg9IjE1MCIgeT0iMTAwIiB3aWR0aD0iMTAwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjNmM3NTdkIi8+Cjx0ZXh0IHg9IjIwMCIgeT0iMjIwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNmM3NTdkIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiPk5vIEltYWdlPC90ZXh0Pgo8L3N2Zz4K';
                          }}
                          decoding="async"
                        />
                        <div className="absolute top-3 right-3">
                          <span className="bg-primary-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
                            {product.category}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 p-4 sm:p-5">
                        <div className="flex flex-col h-full">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-1.5 break-words">{product.name}</h3>
                            <p className="text-xs text-gray-500 mb-2 break-all">Code: {product.code}</p>
                            <p className="text-2xl font-bold text-primary-600 mb-3">Ksh {product.price.toLocaleString()}</p>
                            <p className="text-gray-600 mb-4 line-clamp-3 break-words text-sm">{product.description}</p>
                          </div>
                          <div className="flex justify-end">
                            <Link 
                              to={`/products/${product.id}`} 
                              className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-2.5 px-5 rounded-lg font-semibold hover:from-primary-700 hover:to-primary-800 transition-all duration-200 flex items-center group text-sm"
                            >
                              View Details 
                              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Modern Pagination */}
            {totalPagesFiltered > 1 && (
              <div className="mt-12">
                <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 border border-gray-100">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-sm text-gray-600 text-center sm:text-left">
                      Showing <span className="font-semibold text-gray-900">{startIndex + 1}-{Math.min(endIndex, totalFilteredProducts)}</span> of <span className="font-semibold text-gray-900">{totalFilteredProducts}</span> products
                    </div>
                    
                    <div className="flex items-center space-x-1 sm:space-x-2 overflow-x-auto">
                      {/* Previous Button */}
                      <button
                        onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                        disabled={currentPage === 1}
                        className={`px-3 sm:px-4 py-2 rounded-xl font-medium transition-all duration-200 text-sm whitespace-nowrap ${
                          currentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-primary-500 hover:bg-primary-50'
                        }`}
                      >
                        Previous
                      </button>

                      {/* Page Numbers */}
                      <div className="flex space-x-1 overflow-x-auto">
                        {Array.from({ length: totalPagesFiltered }, (_, i) => i + 1).map((page) => (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3 sm:px-4 py-2 rounded-xl font-medium transition-all duration-200 text-sm whitespace-nowrap ${
                              currentPage === page
                                ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg'
                                : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-primary-500 hover:bg-primary-50'
                            }`}
                          >
                            {page}
                          </button>
                        ))}
                      </div>

                      {/* Next Button */}
                      <button
                        onClick={() => handlePageChange(Math.min(currentPage + 1, totalPagesFiltered))}
                        disabled={currentPage === totalPagesFiltered}
                        className={`px-3 sm:px-4 py-2 rounded-xl font-medium transition-all duration-200 text-sm whitespace-nowrap ${
                          currentPage === totalPagesFiltered
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-primary-500 hover:bg-primary-50'
                        }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Filter Status */}
            {totalFilteredProducts !== products.length && (
              <div className="mt-6 text-center">
                <div className="inline-flex items-center bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm">
                  <Filter className="w-4 h-4 mr-2" />
                  Filtered by: {selectedCategory !== 'All' ? `Category: ${selectedCategory}` : ''} {searchTerm ? `Search: "${searchTerm}"` : ''}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modern CTA Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 py-20 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-3xl p-12 border border-white border-opacity-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Need a Custom Solution?</h2>
            <p className="text-xl md:text-2xl text-primary-100 mb-10 max-w-4xl mx-auto leading-relaxed">
              Our extensive catalog has many more products. Contact us for specific inquiries or custom solutions tailored to your needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact" 
                className="bg-white text-primary-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all duration-200 flex items-center justify-center group"
              >
                Request a Quote 
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link 
                to="/support" 
                className="border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white hover:text-primary-600 transition-all duration-200 flex items-center justify-center group"
              >
                Get Support
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Image Zoom Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={closeImageModal}
              className="absolute -top-4 -right-4 bg-white rounded-full p-2 hover:bg-gray-100 transition-colors z-10 shadow-lg"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
            <div className="bg-white rounded-lg p-4 max-w-2xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">{selectedProductName}</h3>
              <img
                src={fixImageUrl(selectedImage)}
                alt={selectedProductName}   
                className="max-w-full max-h-96 object-contain rounded-lg"
                onClick={closeImageModal}
              />
            </div>
          </div>
        </div>  
      )}

      {/* Scroll to Top FAB */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 z-40 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-xl w-12 h-12 flex items-center justify-center"
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}
    </div>
  );
};

export default ProductsPage;
